import { fetchCollectionPageResult } from '@/domain/market/collection-page';
import { PAGE_SIZE } from '@/domain/market/feed-query';
import {
  paginatedFeedBlockAutoLoad,
  paginatedFeedCanLoadMore,
  paginatedFeedClearAutoLoadBlock,
  paginatedFeedRecordAppend,
  paginatedFeedResetAutoAppendCount,
  paginatedFeedShouldAutoLoad,
} from '@/domain/market/paginated-feed-pagination.js';
import {
  paginatedFeedApplyFetchError,
  paginatedFeedClearListFetchError,
} from '@/domain/market/feed-fetch-error-state.js';
import { resolveFeedScrollLoadThreshold } from '@/constants/feed-pagination.js';
import { getFeedScrollMetrics } from '@/composables/feed-viewport.js';
import { paginatedFeedGetPlatform } from '@/domain/market/paginated-feed-pagination.js';
import { mergeSilentFeedRefreshRows } from '@/domain/market/silent-feed-refresh.js';

/**
 * 分页 Feed Store 共用逻辑（marketFeed / exploreFeed）。
 * 仅处理 collection/page 分页与 scroll loadMore，不含筛选/分类等领域状态。
 */

/** 解除 initialized + loading 卡住导致永不发请求的状态 */
export function paginatedFeedRecoverIfStuck(store) {
  if (!store.initialized && !store.listLoading && !store.loadingMore) return;
  if (store.rawRows.length) return;
  if (!store.listLoading && !store.loadingMore) {
    if (store.initialized) store.initialized = false;
    return;
  }
  // 首屏合法 loading（initialized=false）不误杀，避免 mounted+activated 双 init 取消进行中的请求
  if (!store.initialized) return;
  store.listLoading = false;
  store.loadingMore = false;
  store.initialized = false;
}

/**
 * @param {object} store Pinia store 实例（this）
 * @param {boolean} append
 * @param {object} options
 * @param {() => object} options.getQuery 构建 OpenAPI CollectionQuery
 * @param {string} [options.logTag]
 * @param {boolean} [options.silent]
 * @param {boolean} [options.patchRows]
 *   silent 就地补丁：按 key 更新已有行，保留已加载分页（pageNum/hasMore/autoAppend）；
 *   勿用于 crypto Group 后 silent page 回填（彼处需重写 hasMore）。
 * @param {boolean} [options.showError]
 * @param {(ctx: { state: object, apiRows: object[], hadRows: boolean }) => object[]} [options.resolveRefreshRows]
 *   非 append 时如何写入 rawRows；默认直接用 apiRows
 * @param {number} [options.scrollLoadThreshold] scroll 触底 loadMore 阈值（px）
 */
export async function paginatedFeedFetchPage(store, append = false, options = {}) {
  const {
    getQuery,
    logTag = 'feed',
    silent = false,
    patchRows = false,
    resolveRefreshRows = null,
  } = options;
  const patchExisting = Boolean(silent && patchRows && store.rawRows?.length);

  if (!append) paginatedFeedRecoverIfStuck(store);

  if (append) {
    if (store.loadingMore || !store.hasMore) return;
    store.loadingMore = true;
  } else if (store.listLoading && !silent) {
    return;
  } else if (!silent) {
    store.listLoading = true;
    store.pageNum = 0;
    store.hasMore = true;
    paginatedFeedResetAutoAppendCount(store);
    paginatedFeedClearListFetchError(store);
  } else if (store.listLoading || store.loadingMore) {
    return;
  } else if (patchExisting) {
    paginatedFeedClearListFetchError(store);
  } else {
    store.pageNum = 0;
    store.hasMore = true;
    paginatedFeedResetAutoAppendCount(store);
    paginatedFeedClearListFetchError(store);
  }

  const gen = ++store.loadGeneration;
  const query = typeof getQuery === 'function' ? getQuery(store) : getQuery;

  try {
    const pageSize = options.pageSize ?? PAGE_SIZE;
    const fetchCtx = {
      query,
      append,
      pageNum: store.pageNum,
      rawRowsLength: store.rawRows.length,
      pageSize,
      state: store,
      patchExisting,
      signal: options.signal,
    };
    const pageResult =
      typeof options.fetchPageResult === 'function'
        ? await options.fetchPageResult(fetchCtx)
        : await fetchCollectionPageResult(fetchCtx);
    const {
      apiRows,
      nextPage,
      hasMore,
      replaceRows = false,
      preservePagination = false,
      authoritativeRows = false,
    } = pageResult;
    if (gen !== store.loadGeneration || options.signal?.aborted) return;

    paginatedFeedClearListFetchError(store);

    if (authoritativeRows) {
      store.rawRows = apiRows;
      if (!preservePagination) {
        store.pageNum = nextPage;
        store.hasMore = hasMore;
      }
      if (append && !options.viewportFill) paginatedFeedRecordAppend(store);
      paginatedFeedClearAutoLoadBlock(store);
    } else if (append) {
      // 分钟盘归集等场景：apiRows 已是全量归集结果，需整体替换而非 push 增量
      store.rawRows = replaceRows ? apiRows : store.rawRows.concat(apiRows);
      store.pageNum = nextPage;
      store.hasMore = hasMore;
      if (!options.viewportFill) {
        paginatedFeedRecordAppend(store);
      }
      paginatedFeedClearAutoLoadBlock(store);
    } else {
      const hadRows = store.rawRows.length > 0;
      const previousRows = store.rawRows;
      let nextRows;
      if (patchExisting) {
        nextRows = apiRows.length
          ? mergeSilentFeedRefreshRows(previousRows, apiRows)
          : previousRows;
      } else if (typeof resolveRefreshRows === 'function') {
        nextRows = resolveRefreshRows({ state: store, apiRows, hadRows });
      } else {
        nextRows = apiRows;
      }
      // 默认视图空响应保留旧列表 / silent 就地补丁：不覆盖 pageNum/hasMore
      const keptPreviousList = hadRows && nextRows === previousRows;
      store.rawRows = nextRows;
      if (!keptPreviousList && !patchExisting) {
        store.pageNum = nextPage;
        store.hasMore = hasMore;
      }
    }
    if (typeof options.applyPageResult === 'function') options.applyPageResult(store, pageResult);
  } catch (err) {
    if (gen !== store.loadGeneration || options.signal?.aborted) return;
    console.warn(`[${logTag}] getMarketCollectionPage failed`, err);
    if (append) {
      paginatedFeedBlockAutoLoad(store);
    } else if (!store.rawRows.length) {
      store.rawRows = [];
      store.hasMore = false;
      paginatedFeedApplyFetchError(store, err, { append });
    }
    throw err;
  } finally {
    // 仅当前 generation 的请求可清 loading；stale 非 append 请求不得动 listLoading（reload 竞态）
    if (gen === store.loadGeneration) {
      store.listLoading = false;
      store.loadingMore = false;
      if (!append) store.initialized = true;
    } else if (append) {
      store.loadingMore = false;
    }
  }
}

export async function paginatedFeedLoadMore(store, fetchOptions = {}) {
  if (!paginatedFeedCanLoadMore(store, fetchOptions)) return;
  if (fetchOptions.manual) {
    paginatedFeedClearAutoLoadBlock(store);
  }
  await paginatedFeedFetchPage(store, true, fetchOptions);
}

function defaultShouldIgnoreScrollLoad(container) {
  if (!container) return true;
  const { scrollTop, scrollHeight, clientHeight } = container;
  if (scrollTop > 0 && scrollTop + clientHeight > scrollHeight + 8) return true;
  return false;
}

/** scroll 触底自动 loadMore；threshold 为距底 px，默认约 0.85 视口高（最少 480px） */
export function paginatedFeedOnContainerScroll(
  store,
  container,
  { threshold, fetchOptions, shouldIgnoreScrollLoad } = {}
) {
  if (
    !container ||
    store.listLoading ||
    store.loadingMore ||
    !store.hasMore ||
    !store.rawRows.length
  ) {
    return;
  }
  const ignore =
    typeof shouldIgnoreScrollLoad === 'function'
      ? shouldIgnoreScrollLoad
      : defaultShouldIgnoreScrollLoad;
  if (ignore(container)) return;
  if (!paginatedFeedShouldAutoLoad(store)) return;
  const metrics = getFeedScrollMetrics(container);
  if (!metrics) return;
  const loadThreshold = resolveFeedScrollLoadThreshold(
    { clientHeight: metrics.clientHeight },
    threshold,
    paginatedFeedGetPlatform(store)
  );
  const remain = metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight;
  if (remain < loadThreshold) {
    void paginatedFeedLoadMore(store, fetchOptions);
  }
}
